/**
 * Página /aceptar-invitacion — el invitado crea su contraseña y entra.
 *
 * Es la puerta de entrada para un funcionario al que el equipo Kaptar
 * invitó desde el panel admin (`POST /admin/tenants/{id}/invite-member`).
 *
 * Flujo:
 * 1. Supabase envía un email de invitación con un link de aceptación.
 *    Al hacer click, Supabase verifica el token y redirige a
 *    `https://app.kaptar.com.co/aceptar-invitacion#access_token=...&type=invite`.
 * 2. El SDK de Supabase JS (`detectSessionInUrl: true`) parsea el hash al
 *    cargar y establece la sesión del invitado (evento `SIGNED_IN`). El
 *    usuario queda autenticado pero SIN contraseña definida.
 * 3. Mostramos un form de "crea tu contraseña".
 * 4. Submit → `supabase.auth.updateUser({ password })`.
 * 5. Al éxito NO cerramos sesión (a diferencia de reset-password): el
 *    invitado acaba de activar su cuenta, así que lo dejamos entrar
 *    directo. Navegamos a `/` y los gates existentes (AuthedRedirect →
 *    /radar, OnboardingGate) lo rutean igual que a un login normal.
 *
 * Por qué una ruta dedicada y no reusar /reset-password: el copy y el
 * destino post-submit son distintos (bienvenida + auto-login vs reset +
 * re-login). El mecanismo de token sí es análogo.
 *
 * IMPORTANTE (config Supabase): `https://app.kaptar.com.co/aceptar-invitacion`
 * debe estar en Authentication → URL Configuration → Redirect URLs
 * (o cubierto por un wildcard). Si no, Supabase cae al Site URL raíz.
 *
 * Edge cases:
 * - Si llega sin token válido (URL tecleada, link expirado): mostramos
 *   un estado de "invitación inválida o expirada" con la salida
 *   self-service — pedir un enlace nuevo desde /forgot-password con el
 *   mismo correo — y soporte como segunda opción (7-sep-2026).
 * - Si Supabase rechaza la contraseña (política del proyecto, sesión
 *   vencida, demasiados intentos), `describePasswordError` traduce el
 *   motivo concreto en vez de "intenta en unos minutos".
 */
import { type FormEvent, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";

import { describePasswordError } from "@/lib/auth-errors";
import { supabase } from "@/lib/supabase";

const MIN_PASSWORD_LENGTH = 8;
/** Ventana para que el SDK establezca la sesión desde el hash. */
const SESSION_DETECT_TIMEOUT_MS = 3000;

export function AceptarInvitacion() {
  const navigate = useNavigate();

  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  /** true cuando reintentar no sirve: hay que pedir un enlace nuevo. */
  const [recoveryHint, setRecoveryHint] = useState(false);
  /** true cuando ya hay sesión válida del invite (listo para crear pass). */
  const [sessionReady, setSessionReady] = useState(false);
  /** true cuando terminó la ventana de detección (con o sin sesión). */
  const [detectionDone, setDetectionDone] = useState(false);

  useEffect(() => {
    let active = true;

    // 1. ¿Ya quedó la sesión establecida desde el hash al cargar?
    void supabase.auth.getSession().then(({ data }) => {
      if (!active) return;
      if (data.session) {
        setSessionReady(true);
        setDetectionDone(true);
      }
    });

    // 2. Escuchar el establecimiento asíncrono (SIGNED_IN para invite).
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((event, session) => {
      if (!active) return;
      if (
        session &&
        (event === "SIGNED_IN" ||
          event === "INITIAL_SESSION" ||
          event === "USER_UPDATED" ||
          event === "PASSWORD_RECOVERY")
      ) {
        setSessionReady(true);
        setDetectionDone(true);
      }
    });

    // 3. Fallback: si en la ventana no hubo sesión, asumimos token inválido.
    const timeout = window.setTimeout(() => {
      if (active) setDetectionDone(true);
    }, SESSION_DETECT_TIMEOUT_MS);

    return () => {
      active = false;
      subscription.unsubscribe();
      window.clearTimeout(timeout);
    };
  }, []);

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setErrorMsg(null);
    setRecoveryHint(false);

    if (password.length < MIN_PASSWORD_LENGTH) {
      setErrorMsg(`La contraseña debe tener al menos ${MIN_PASSWORD_LENGTH} caracteres.`);
      return;
    }
    if (password !== confirm) {
      setErrorMsg("Las dos contraseñas no coinciden.");
      return;
    }

    setIsSubmitting(true);
    const { error } = await supabase.auth.updateUser({ password });
    setIsSubmitting(false);

    if (error) {
      // Supabase dice qué falló (política de contraseña, sesión vencida,
      // demasiados intentos); se lo traducimos en vez de mandarlo a
      // "esperar unos minutos" (7-sep-2026).
      const copy = describePasswordError(error, MIN_PASSWORD_LENGTH);
      setErrorMsg(copy.message);
      setRecoveryHint(copy.recoveryHint);
      return;
    }

    // Auto-login: el invitado ya activó su cuenta. Lo dejamos entrar; los
    // gates (`AuthedRedirect` → /radar, `OnboardingGate`) deciden el destino.
    navigate("/", { replace: true });
  }

  // Estado: token inválido o expirado.
  if (detectionDone && !sessionReady) {
    return (
      <main className="flex min-h-screen items-center justify-center bg-surface p-6">
        <div className="w-full max-w-md">
          <section className="rounded-2xl border border-amber-200 bg-amber-50 p-6">
            <h2 className="mb-2 text-lg font-bold text-amber-900">
              Invitación inválida o expirada
            </h2>
            <p className="text-sm text-amber-800">
              No detectamos una invitación válida. El enlace puede haber expirado o ya haber sido
              usado. Puedes pedir uno nuevo tú mismo: escribe el mismo correo al que llegó la
              invitación y te enviamos un enlace para crear tu contraseña.
            </p>
            <div className="mt-5 flex flex-col gap-2 sm:flex-row">
              <Link
                to="/forgot-password"
                className="rounded-md bg-cta-600 px-4 py-2 text-center text-sm font-semibold text-white hover:bg-cta-700"
              >
                Pedir un enlace nuevo
              </Link>
              <a
                href="mailto:soporte@kaptar.com.co"
                className="rounded-md border border-amber-300 px-4 py-2 text-center text-sm font-semibold text-amber-900 hover:bg-amber-100"
              >
                Contactar soporte
              </a>
            </div>
          </section>
        </div>
      </main>
    );
  }

  return (
    <main className="flex min-h-screen items-center justify-center bg-surface p-6">
      <div className="w-full max-w-md">
        <header className="mb-8 flex items-center gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-brand-600">
            <svg viewBox="0 0 24 24" fill="none" className="h-6 w-6">
              <path d="M12 3L4 8l8 5 8-5-8-5z" fill="white" opacity="0.9" />
              <path d="M4 13l8 5 8-5" stroke="white" strokeWidth="1.8" strokeLinecap="round" />
            </svg>
          </div>
          <div>
            <h1 className="text-2xl font-extrabold tracking-tight text-slate-900">Kaptar</h1>
            <p className="text-xs text-slate-500">
              La forma inteligente de captar recursos públicos
            </p>
          </div>
        </header>

        <form
          onSubmit={handleSubmit}
          className="rounded-2xl border border-slate-200 bg-white p-6 shadow-xs"
          aria-labelledby="invite-title"
        >
          <p className="mb-1 text-xs font-semibold tracking-wider text-brand-600 uppercase">
            Bienvenido a Kaptar
          </p>
          <h2 id="invite-title" className="mb-1 text-lg font-bold text-slate-900">
            Crea tu contraseña
          </h2>
          <p className="mb-6 text-sm text-slate-600">
            Es el último paso para activar tu cuenta. Mínimo {MIN_PASSWORD_LENGTH} caracteres — usa
            letras, números y símbolos.
          </p>

          <label className="mb-3 block">
            <span className="mb-1 block text-sm font-medium text-slate-700">Contraseña</span>
            <input
              type="password"
              required
              autoComplete="new-password"
              minLength={MIN_PASSWORD_LENGTH}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:border-brand-600 focus:ring-1 focus:ring-brand-600 focus:outline-hidden"
            />
          </label>

          <label className="mb-5 block">
            <span className="mb-1 block text-sm font-medium text-slate-700">
              Confirmar contraseña
            </span>
            <input
              type="password"
              required
              autoComplete="new-password"
              minLength={MIN_PASSWORD_LENGTH}
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:border-brand-600 focus:ring-1 focus:ring-brand-600 focus:outline-hidden"
            />
          </label>

          {errorMsg && (
            <div
              role="alert"
              className="mb-4 rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-800"
            >
              <p>{errorMsg}</p>
              {recoveryHint && (
                <p className="mt-2">
                  Pide un enlace nuevo con el mismo correo desde{" "}
                  <Link to="/forgot-password" className="font-semibold underline">
                    ¿Olvidaste tu contraseña?
                  </Link>{" "}
                  y crea tu contraseña ahí. No necesitas escribir a soporte.
                </p>
              )}
            </div>
          )}

          <button
            type="submit"
            disabled={isSubmitting || !sessionReady}
            className="w-full rounded-md bg-cta-600 px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-cta-700 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {isSubmitting ? "Activando tu cuenta…" : "Activar mi cuenta y entrar"}
          </button>

          {!sessionReady && !detectionDone && (
            <p className="mt-3 text-center text-xs text-slate-500">Verificando tu invitación…</p>
          )}
        </form>
      </div>
    </main>
  );
}
